import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { faPencil, faSearch } from "@fortawesome/pro-light-svg-icons";
import { faCheckCircle, faSortDown, faSortUp, faSortUpDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Squad,
  SquadList as ApiSquadList,
  League,
  LeagueListElement,
  LeagueListMinimum
} from "../../../../client/api";
import { translate } from "../../../../config/translator";
import { formatDate, translatePlayerPosition } from "../../../../config/utils";
import { ClavaContext } from "../../../../config/contexts";
import { pathPlayerView } from "../../../../config/routes";
import Loading from "../../Loading";
import ClavaTextInput from "../../ClavaTextInput";
import PlayerImage from "../../PlayerImage";
import ScaleFade from "../../Animated/ScaleFade";

type SortField = "position" | "given" | "family" | "birth";
type SortDir = "asc" | "desc";

type SquadListProps = {
  squad?: ApiSquadList;
  filteredSquad: ApiSquadList;
  leagueForPosition?: League | LeagueListElement | LeagueListMinimum | boolean;
  loading: boolean;
  q: string;
  onChangeQ: (q: string) => void;
  sortField: SortField;
  sortDir: SortDir;
  onSortGiven: () => void;
  onSortFamily: () => void;
  onSortBirth?: () => void;
  onSortPosition: () => void;
  onEdit?: (s: Squad) => void;
  onClickNew?: () => void;
  showNewButton?: boolean;
  showBirthColumn?: boolean;
  showTitle?: boolean;
};

const SquadList: React.FC<SquadListProps> = ({
  squad,
  filteredSquad,
  leagueForPosition,
  loading,
  q,
  onChangeQ,
  sortField,
  sortDir,
  onSortGiven,
  onSortFamily,
  onSortBirth,
  onSortPosition,
  onEdit,
  onClickNew,
  showNewButton = true,
  showBirthColumn = true,
  showTitle = true,
}) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();

  return (
    <div className="my-2 px-4 squad-list">
      <div className="flex flex-row items-center justify-start">
        {showTitle && (
          <h3 className="font-semibold text-sm">{translate("squad", l)} {!!squad && (
            <span className="text-font-600 dark:text-font-600-dark font-normal"> ({squad.length})</span>)}
          </h3>
        )}
        <div className="flex-1" />
        {showNewButton && (
          <button onClick={onClickNew} type="button"
                  className="text-primary hover:bg-primary-10 rounded-xl my-2 mx-2 py-2 px-4">{translate("newPlayer", l)}</button>
        )}

        <ClavaTextInput className="max-w-48" iconL={faSearch} onChange={onChangeQ} value={q}
                        placeholder={translate("search", l)} type="text" />
      </div>
      {loading ? (<Loading small />) : (
        <div
          className="w-full mt-4 px-2 border-b border-b-light-gray dark:border-b-light-gray-dark h-10 flex flex-row items-center justify-start">
          <div className="w-14" />
          <div className="flex-1 flex flex-row items-center justify-start" onClick={onSortGiven} onKeyUp={onSortGiven}
               tabIndex={0} role="button">
            <span className=" text-font-600 dark:text-font-600-dark">{translate("givenName", l)}</span>
            <FontAwesomeIcon icon={sortField === "given" ? (sortDir === "desc" ? faSortUp : faSortDown) : faSortUpDown}
                             className="text-font-600 dark:text-font-600-dark ml-4 squadlist-icon" />
          </div>
          <div className="flex-1 flex flex-row items-center justify-start" onClick={onSortFamily} onKeyUp={onSortFamily}
               tabIndex={0}
               role="button">
            <span className="text-font-600 dark:text-font-600-dark">{translate("familyName", l)}</span>
            <FontAwesomeIcon icon={sortField === "family" ? (sortDir === "desc" ? faSortUp : faSortDown) : faSortUpDown}
                             className="text-font-600 dark:text-font-600-dark ml-4 squadlist-icon" />
          </div>
          {showBirthColumn && (
            onSortBirth ? (
              <div className="flex-1 flex flex-row items-center justify-start" onClick={onSortBirth} onKeyUp={onSortBirth}
                   tabIndex={0}
                   role="button">
                <span className="text-font-600 dark:text-font-600-dark">{translate("birthday", l)}</span>
                <FontAwesomeIcon icon={sortField === "birth" ? (sortDir === "desc" ? faSortUp : faSortDown) : faSortUpDown}
                                 className="text-font-600 dark:text-font-600-dark ml-4 squadlist-icon" />
              </div>
            ) : (
              <div className="flex-1 flex flex-row items-center justify-start">
                <span className="text-font-600 dark:text-font-600-dark">{translate("birthday", l)}</span>
              </div>
            )
          )}
          <div className="flex-1 flex flex-row items-center justify-start" onClick={onSortPosition}
               onKeyUp={onSortPosition} tabIndex={0}
               role="button">
            <span className="text-font-600 dark:text-font-600-dark">{translate("position", l)}</span>
            <FontAwesomeIcon icon={sortField === "position" ? (sortDir === "desc" ? faSortUp : faSortDown) : faSortUpDown}
                             className="text-font-600 dark:text-font-600-dark ml-4 squadlist-icon" />
          </div>
          {onEdit && (<div className="w-11" />)}
        </div>
      )}
      {filteredSquad.map((sq) => (
        <div
          className={`w-full border-b px-2 border-b-light-gray dark:border-b-light-gray-dark h-14 flex flex-row items-center justify-start hover:bg-bg dark:hover:bg-bg-dark ${!onEdit ? "cursor-pointer" : ""}`}
          key={`squad-${sq.player.id}`}
          onClick={!onEdit ? () => navigate(pathPlayerView(sq.player.id)) : undefined}
          onKeyDown={!onEdit ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(pathPlayerView(sq.player.id));
            }
          } : undefined}
          tabIndex={!onEdit ? 0 : undefined}
          role={!onEdit ? "button" : undefined}
        >
          <div 
            className="relative w-14 flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              navigate(pathPlayerView(sq.player.id));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                navigate(pathPlayerView(sq.player.id));
              }
            }}
            tabIndex={0}
            role="button"
          >
            <ScaleFade isOpen hoverOnContainer className="w-10 h-10">
              <PlayerImage width={40} image={sq.player.photo ?? undefined} className="rounded-full" round />
            </ScaleFade>
            {!!sq.player.user && (
              <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 absolute text-green bottom-0 right-2" />
            )}
          </div>
          <div className="flex-1">
            <span className="text-sm">{sq.player.givenName}</span>
          </div>
          <div className="flex-1">
            <span className="text-sm">{sq.player.familyName}</span>
          </div>
          {showBirthColumn && (
            <div className="flex-1">
              <span className="text-sm">{sq.player.birthdate ? formatDate(sq.player.birthdate, l, false, false, true) : " - "}</span>
            </div>
          )}
          <div className="flex-1">
            <span className="text-sm">
              {translatePlayerPosition(
                sq.player.playerPosition.key,
                leagueForPosition,
                l
              )}
            </span>
          </div>
          {onEdit && (
            <button type="button" onClick={() => onEdit(sq)}
                    className="w-9 h-9 mx-1 rounded-full border border-light-gray dark:border-light-gray-dark hover:bg-primary-10 flex items-center justify-center">
              <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default SquadList;


